/* eslint-disable */

/**
 * reviews — staff-facing Review Management state + API calls (milestone A6).
 * Canonical plan: c:\Users\nilay\Documents\teamio-reviews.md.
 *
 * Backing service: reviews-service (teamio-serverless-api). Every endpoint wraps
 * its payload in { success, data } (common.createSuccessResponse), so the useful
 * shape is always `response.data.data` — same convention as store/reviewLanding.js.
 *
 * The Reviews page is naturally clinic-scoped: siteID comes from the current
 * Switch-Site selection (customer.siteID), and the service keys every review_*
 * table on that clinic SiteID.
 */

import axios from 'axios';

axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL;

/** Unwrap the { success, data } envelope; throw on an unsuccessful body. */
function unwrap(response) {
  const payload = response && response.data;
  if (payload && payload.success) return payload.data;
  throw new Error((payload && payload.message) || 'Request failed');
}

// Structured fields a reviews-service error body may carry (common.createErrorResponse puts
// them at the TOP level: { code, message, reason?, note?, expiresAt?, lastSentAt? }).
const API_ERROR_FIELDS = ['code', 'reason', 'note', 'expiresAt', 'lastSentAt'];

/**
 * Normalize an axios error into an Error carrying the server's sentence as `.message` plus
 * `.status` and every API_ERROR_FIELDS key, so callers branch on `err.code`
 * (OPTED_OUT, REPLIED_STOP, OUT_OF_WINDOW, ALREADY_SENT, VISIT_COMPLETED, INVALID_UNTIL_DATE …)
 * instead of axios's "Request failed with status code 409" boilerplate.
 * An error with no response (network, or unwrap's own throw) is returned unchanged.
 */
export function toReviewsApiError(err, fallbackMessage) {
  const resp = err && err.response;
  if (!resp || resp.data === undefined || resp.data === null) return err;
  // API Gateway proxy body is JSON; axios usually parses it, but tolerate a string too.
  let body = resp.data;
  if (typeof body === 'string') {
    try { body = JSON.parse(body); } catch (e) { body = { message: resp.data }; }
  }
  const e = new Error((body && typeof body.message === 'string' && body.message) || fallbackMessage || 'Request failed');
  e.status = resp.status;
  API_ERROR_FIELDS.forEach((k) => {
    e[k] = body && body[k] !== undefined ? body[k] : undefined;
  });
  // ⛔ No `e.response`: the axios response carries `config.data` — the request body (patNum, the
  // staff note) — and components log errors to the console, which LogRocket captures.
  return e;
}

/** POST + unwrap, with errors normalized through toReviewsApiError. */
async function postReviews(url, payload, fallbackMessage) {
  try {
    return unwrap(await axios.post(url, payload, { crossdomain: true }));
  } catch (err) {
    throw toReviewsApiError(err, fallbackMessage);
  }
}

export default {
  namespaced: true,

  state: {
    // { enabled, channels, smsTemplateID, emailTemplateID, snapshots, timing,
    //   followUps, excludedProcCodes, gateThreshold, reviewLinks, privateFeedback }
    settings: null,
    paidFor: false,

    // get-reviews-dashboard payload (totals + weekly series + Google placeholders)
    dashboard: null,

    // get-review-requests
    requests: [],
    requestsTotal: 0,

    // get-private-feedback
    privateFeedback: [],

    // get-review-proc-codes → [{ codeNum, procCode, descript, procCat }]
    procCodes: [],

    // get-google-reviews-connection →
    // { connected, status, accountName, locationName, locationTitle, placeId,
    //   connectedAt, lastError, availableLocations? } — status ∈
    //   connected|needs_location|error|disconnected. Never carries a token.
    googleConnection: null,

    // get-review-do-not-send-list → { rows, total, counts: { staff, stop } }
    doNotSend: { rows: [], total: 0, counts: { staff: 0, stop: 0 } },

    // get-review-upcoming → { siteID, date, timezone, automationEnabled, sendTimeLabel, rows }
    upcoming: null,

    // Last-issued request number per list, so a slow response for an old filter/day can
    // never overwrite the one the office is looking at now.
    requestSeq: { doNotSend: 0, upcoming: 0 },

    loading: {
      settings: false,
      dashboard: false,
      requests: false,
      feedback: false,
      procCodes: false,
      saving: false,
      googleConnection: false,
      doNotSend: false,
      upcoming: false,
    },
  },

  mutations: {
    SET_LOADING(state, { key, value }) {
      state.loading = { ...state.loading, [key]: value };
    },
    SET_SETTINGS(state, { settings, paidFor }) {
      state.settings = settings;
      if (paidFor !== undefined) state.paidFor = paidFor;
    },
    SET_DASHBOARD(state, data) {
      state.dashboard = data;
    },
    SET_REQUESTS(state, { requests, total }) {
      state.requests = requests || [];
      state.requestsTotal = total || 0;
    },
    SET_PRIVATE_FEEDBACK(state, feedback) {
      state.privateFeedback = feedback || [];
    },
    // Optimistic in-place resolve/re-open so the inbox reacts before the round-trip.
    PATCH_FEEDBACK(state, { feedbackID, patch }) {
      const idx = state.privateFeedback.findIndex((f) => f.feedbackID === feedbackID);
      if (idx !== -1) {
        state.privateFeedback.splice(idx, 1, { ...state.privateFeedback[idx], ...patch });
      }
    },
    SET_PROC_CODES(state, procCodes) {
      state.procCodes = procCodes || [];
    },
    SET_GOOGLE_CONNECTION(state, connection) {
      state.googleConnection = connection || null;
    },
    BUMP_REQUEST_SEQ(state, key) {
      state.requestSeq = { ...state.requestSeq, [key]: (state.requestSeq[key] || 0) + 1 };
    },
    SET_DO_NOT_SEND(state, data) {
      const d = data || {};
      state.doNotSend = {
        rows: Array.isArray(d.rows) ? d.rows : [],
        total: d.total || 0,
        counts: { staff: (d.counts && d.counts.staff) || 0, stop: (d.counts && d.counts.stop) || 0 },
      };
    },
    SET_UPCOMING(state, data) {
      state.upcoming = data || null;
    },
    // Optimistic per-row opt-out flip (Requests table); rolled back on error.
    PATCH_REQUEST_OPTOUT(state, { requestID, optedOut }) {
      const idx = state.requests.findIndex((r) => r.requestID === requestID);
      if (idx !== -1) {
        state.requests.splice(idx, 1, { ...state.requests[idx], optedOut });
      }
    },
  },

  actions: {
    async getReviewsSettings({ commit }, { siteID }) {
      commit('SET_LOADING', { key: 'settings', value: true });
      try {
        const data = unwrap(await axios.post('/get-reviews-settings', { siteID }, { crossdomain: true }));
        commit('SET_SETTINGS', { settings: data.settings, paidFor: data.paidFor });
        return data;
      } finally {
        commit('SET_LOADING', { key: 'settings', value: false });
      }
    },

    async saveReviewsSettings({ commit }, { siteID, settings }) {
      commit('SET_LOADING', { key: 'saving', value: true });
      try {
        const data = unwrap(await axios.post('/save-reviews-settings', { siteID, settings }, { crossdomain: true }));
        // Service echoes the merged, re-verified settings — adopt them.
        commit('SET_SETTINGS', { settings: data.settings });
        return data;
      } finally {
        commit('SET_LOADING', { key: 'saving', value: false });
      }
    },

    async getReviewsDashboard({ commit }, { siteID, rangeDays }) {
      commit('SET_LOADING', { key: 'dashboard', value: true });
      try {
        const data = unwrap(await axios.post(
          '/get-reviews-dashboard',
          { siteID, rangeDays: rangeDays || 30 },
          { crossdomain: true }
        ));
        commit('SET_DASHBOARD', data);
        return data;
      } finally {
        commit('SET_LOADING', { key: 'dashboard', value: false });
      }
    },

    async getReviewRequests({ commit }, { siteID, limit, offset, status }) {
      commit('SET_LOADING', { key: 'requests', value: true });
      try {
        const payload = { siteID, limit: limit || 50, offset: offset || 0 };
        if (status) payload.status = status;
        const data = unwrap(await axios.post('/get-review-requests', payload, { crossdomain: true }));
        commit('SET_REQUESTS', { requests: data.requests, total: data.total });
        return data;
      } finally {
        commit('SET_LOADING', { key: 'requests', value: false });
      }
    },

    // Cancel ONE pending request (the per-visit veto). Errors propagate so the caller can
    // surface the server's 409 sentence — "already sent", "someone just cancelled it" — which
    // is real information about a race, not a generic failure to swallow.
    async cancelReviewRequest(_, { siteID, requestID, employeeID }) {
      const payload = { siteID, requestID };
      if (employeeID) payload.employeeID = employeeID;
      return unwrap(await axios.post('/cancel-review-request', payload, { crossdomain: true }));
    },

    async getPrivateFeedback({ commit }, { siteID, status }) {
      commit('SET_LOADING', { key: 'feedback', value: true });
      try {
        const payload = { siteID };
        if (status) payload.status = status;
        const data = unwrap(await axios.post('/get-private-feedback', payload, { crossdomain: true }));
        commit('SET_PRIVATE_FEEDBACK', data.feedback);
        return data;
      } finally {
        commit('SET_LOADING', { key: 'feedback', value: false });
      }
    },

    async resolvePrivateFeedback(_ctx, { siteID, feedbackID, status, responseNote, employeeID }) {
      const payload = { siteID, feedbackID, status };
      if (responseNote !== undefined) payload.responseNote = responseNote;
      if (employeeID !== undefined) payload.employeeID = employeeID;
      return unwrap(await axios.post('/resolve-private-feedback', payload, { crossdomain: true }));
    },

    async getReviewProcCodes({ commit }, { siteID }) {
      commit('SET_LOADING', { key: 'procCodes', value: true });
      try {
        const data = unwrap(await axios.post('/get-review-proc-codes', { siteID }, { crossdomain: true }));
        commit('SET_PROC_CODES', data.procCodes);
        return data;
      } finally {
        commit('SET_LOADING', { key: 'procCodes', value: false });
      }
    },

    // ─────────────────────────── Phase B1: Google Business Profile connect ──────
    // Mirrors the Slack OAuth-connect idiom (store/slack.js): the FE asks the
    // backend for a consent URL and does a full-page redirect; the backend
    // callback 302s back to /app/reviews?tab=settings&google=<status>&location=…
    // and the component hydrates connection status via getGoogleConnection.

    /** Read-only connection status (never contains a token). Hydrates on mount
     *  and after the OAuth redirect returns. */
    async getGoogleConnection({ commit }, { siteID }) {
      commit('SET_LOADING', { key: 'googleConnection', value: true });
      try {
        const data = unwrap(await axios.post('/get-google-reviews-connection', { siteID }, { crossdomain: true }));
        commit('SET_GOOGLE_CONNECTION', data.connection);
        return data;
      } finally {
        commit('SET_LOADING', { key: 'googleConnection', value: false });
      }
    },

    /** Start the OAuth flow — returns { authUrl }; the caller does
     *  window.location.href = authUrl (full-page redirect, not a popup). */
    async startGoogleConnect({ commit }, { siteID, returnUrl }) {
      commit('SET_LOADING', { key: 'googleConnection', value: true });
      try {
        const data = unwrap(await axios.post(
          '/google-reviews-auth-start',
          { siteID, returnUrl: returnUrl || window.location.origin },
          { crossdomain: true }
        ));
        return data;
      } finally {
        commit('SET_LOADING', { key: 'googleConnection', value: false });
      }
    },

    /** Resolve a multi-location account (status === 'needs_location') by picking
     *  a location; echoes the refreshed connection. */
    async selectGoogleLocation({ commit }, { siteID, locationName }) {
      commit('SET_LOADING', { key: 'googleConnection', value: true });
      try {
        const data = unwrap(await axios.post(
          '/google-reviews-select-location',
          { siteID, locationName },
          { crossdomain: true }
        ));
        commit('SET_GOOGLE_CONNECTION', data.connection);
        return data;
      } finally {
        commit('SET_LOADING', { key: 'googleConnection', value: false });
      }
    },

    /** Disconnect Google; clears the cached connection. */
    async disconnectGoogleReviews({ commit }, { siteID }) {
      commit('SET_LOADING', { key: 'googleConnection', value: true });
      try {
        const data = unwrap(await axios.post('/disconnect-google-reviews', { siteID }, { crossdomain: true }));
        commit('SET_GOOGLE_CONNECTION', null);
        return data;
      } finally {
        commit('SET_LOADING', { key: 'googleConnection', value: false });
      }
    },

    /**
     * A7 — per-patient org-wide opt-out toggle. Used by both the Requests table
     * (optimistic flip + rollback owned by the component) and ReviewRequestModal
     * on the Patient Profile. Backend cancels pending schedules + flips still-
     * 'scheduled' rows to 'opted_out' family-wide when optedOut=true. Do NOT
     * commit any state here — callers own their own optimistic UI.
     */
    async setReviewPatientOptout(_ctx, { siteID, patNum, optedOut, employeeID, reason, note, duration, untilDate }) {
      const payload = { siteID, patNum, optedOut };
      if (employeeID !== undefined) payload.employeeID = employeeID;
      // Do-not-send entry fields (2026-09-14). Only sent when present, so the old
      // { optedOut } payload still means "Forever, no reason" server-side.
      const present = (v) => v !== undefined && v !== null && v !== '';
      if (optedOut) {
        if (present(reason)) payload.reason = reason;
        if (present(note)) payload.note = note;
        if (present(duration)) payload.duration = duration;
        if (duration === 'until' && present(untilDate)) payload.untilDate = untilDate;
      }
      // 400 INVALID_REASON | NOTE_REQUIRED | NOTE_TOO_LONG | INVALID_DURATION | INVALID_UNTIL_DATE
      return postReviews('/set-review-patient-optout', payload, 'Could not update this patient.');
    },

    /**
     * The Do not send list: staff entries (source 'staff') + patients who replied STOP
     * (source 'stop', read-only). → { rows, total, counts: { staff, stop } }
     */
    async getDoNotSendList({ commit, state }, { siteID, source, search, limit, offset }) {
      commit('BUMP_REQUEST_SEQ', 'doNotSend');
      const seq = state.requestSeq.doNotSend;
      commit('SET_LOADING', { key: 'doNotSend', value: true });
      try {
        const payload = { siteID, source: source || 'all', limit: limit || 50, offset: offset || 0 };
        if (search) payload.search = search;
        const data = await postReviews('/get-review-do-not-send-list', payload, 'Could not load the Do not send list.');
        if (seq === state.requestSeq.doNotSend) commit('SET_DO_NOT_SEND', data);
        return data;
      } finally {
        if (seq === state.requestSeq.doNotSend) commit('SET_LOADING', { key: 'doNotSend', value: false });
      }
    },

    /**
     * One day of appointments (today … today + 7, site timezone) with the predicted review
     * state per visit. 400 INVALID_DATE outside the window.
     */
    async getUpcomingAppointments({ commit, state }, { siteID, date }) {
      commit('BUMP_REQUEST_SEQ', 'upcoming');
      const seq = state.requestSeq.upcoming;
      commit('SET_LOADING', { key: 'upcoming', value: true });
      try {
        const data = await postReviews('/get-review-upcoming', { siteID, date }, 'Could not load upcoming appointments.');
        if (seq === state.requestSeq.upcoming) commit('SET_UPCOMING', data);
        return data;
      } finally {
        if (seq === state.requestSeq.upcoming) commit('SET_LOADING', { key: 'upcoming', value: false });
      }
    },

    /**
     * Skip ONE upcoming visit's automatic request. → { aptNum, requestID, alreadySkipped, ruleReason }
     * 400 OUT_OF_WINDOW · 404 NOT_FOUND · 409 ALREADY_SENT — thrown with `.code` + the server's message.
     */
    async skipReviewForAppointment(_ctx, { siteID, aptNum, employeeID }) {
      const payload = { siteID, aptNum };
      if (employeeID !== undefined) payload.employeeID = employeeID;
      return postReviews('/skip-review-for-appointment', payload, 'Could not skip this visit.');
    },

    /** Undo a staff skip. → { aptNum, unskipped: true } · 404 NOT_SKIPPED · 409 VISIT_COMPLETED */
    async unskipReviewForAppointment(_ctx, { siteID, aptNum, employeeID }) {
      const payload = { siteID, aptNum };
      if (employeeID !== undefined) payload.employeeID = employeeID;
      return postReviews('/unskip-review-for-appointment', payload, 'Could not undo the skip.');
    },

    /**
     * A7 — per-patient reviews status for ReviewRequestModal: opt-out flag,
     * per-channel availability + reason codes, recent-request history, and the
     * canSendNow/blockedReason gate. Returns the raw data object (no state
     * commit — the modal is self-contained).
     */
    async getReviewPatientStatus(_ctx, { siteID, patNum }) {
      return unwrap(await axios.post('/get-review-patient-status', { siteID, patNum }, { crossdomain: true }));
    },

    /**
     * A7 — manual "Send review request now". Works even when automation is
     * paused. On a non-2xx the backend returns a structured body
     * ({ code, message, lastSentAt? }); axios throws, so normalize it into an
     * Error carrying `.code` / `.status` / `.lastSentAt` so the modal can branch
     * (RECENT_REQUEST → offer "Send anyway" with force:true).
     */
    async sendReviewRequest(_ctx, { siteID, patNum, channels, employeeID, force }) {
      const payload = { siteID, patNum };
      if (Array.isArray(channels) && channels.length) payload.channels = channels;
      if (employeeID !== undefined) payload.employeeID = employeeID;
      if (force) payload.force = true;
      // 409 OPTED_OUT carries { reason, note, expiresAt }; 409 REPLIED_STOP; 409 RECENT_REQUEST { lastSentAt }.
      return postReviews('/send-review-request', payload, 'Failed to send review request');
    },
  },
};
